import { useState, useEffect, useRef } from 'react'
import { InputTextarea } from 'primereact/inputtextarea'
import { Button } from 'primereact/button'
import { sendChatMessage, stopStreaming } from '../../redux/actions/chatbot'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import { SendIcon } from '../icons/SendIcon'
import { AppState } from '@Techedge/digitalportal-shared'
import { ThunkDispatch } from 'redux-thunk'
import { AnyAction } from 'redux'
import { ChatbotState } from '../../redux/states/chatbot'

const StopIcon = ({ size = 24, color = 'currentColor' }) => (
	<svg
		width={size}
		height={size}
		viewBox="0 0 24 24"
		fill="none"
		xmlns="http://www.w3.org/2000/svg">
		<rect x="6" y="6" width="12" height="12" rx="2" fill={color} />
	</svg>
)

const ChatInput = () => {
	const intl = useIntl()
	const [value, setValue] = useState('')
	const eventSourceRef = useRef<EventSource | null>(null)
	const [isStreaming, setIsStreaming] = useState(false)
	const user = useSelector((state: AppState) => state.userReducer.user)
	const language = user.language
	const dispatch =
		useDispatch<ThunkDispatch<ChatbotState, unknown, AnyAction>>()
	const conversationId = useSelector(
		(state: { chatbotReducer: ChatbotState }) =>
			state.chatbotReducer.conversation_id
	)

	const messages = useSelector(
		(state: { chatbotReducer: ChatbotState }) =>
			state.chatbotReducer.messages
	)
	const botMessages = messages.filter((msg: any) => msg.role === 'bot')
	const hasBotTyping = botMessages.some((msg: any) => msg.isTyping)
	const isTyping = useSelector(
		(state: { chatbotReducer: ChatbotState }) =>
			state.chatbotReducer.isTyping
	)
	const isStreamingActive = isTyping || hasBotTyping

	useEffect(() => {
		setIsStreaming(isStreamingActive)
	}, [isStreamingActive])

	useEffect(() => {
		return () => {
			if (eventSourceRef.current) {
				eventSourceRef.current.close()
			}
		}
	}, [])

	const handleSend = () => {
		if (!value.trim()) return
		dispatch(
			sendChatMessage(value, language, conversationId, user.nickname)
		)
		setValue('')
		setIsStreaming(true)
	}

	const handleStop = () => {
		if (eventSourceRef.current) {
			eventSourceRef.current.close()
			eventSourceRef.current = null
		}
		setIsStreaming(false)

		const lastTypingMessage = messages
			.filter((msg: any) => msg.role === 'bot')
			.reverse()
			.find((msg: any) => msg.isTyping)

		if (lastTypingMessage) {
			dispatch(
				stopStreaming(lastTypingMessage.id, lastTypingMessage.text)
			)
		} else {
			dispatch(stopStreaming('', ''))
		}
	}

	return (
		<div style={{ position: 'relative', padding: '8px' }}>
			<InputTextarea
				className="chat-input"
				value={value}
				onChange={(e) => setValue(e.target.value)}
				autoResize
				rows={1}
				placeholder={intl.formatMessage({
					id: 'chatbot.inputPlaceholder',
					defaultMessage: 'Ask anything...',
				})}
				style={{ width: '100%', paddingRight: '40px' }}
				onKeyDown={(e) => {
					if (e.key === 'Enter' && !e.shiftKey) {
						e.preventDefault()
						handleSend()
					}
				}}
				disabled={isStreaming}
			/>
			<Button
				onClick={isStreaming ? handleStop : handleSend}
				className={`p-button-rounded p-button-text ${
					isStreaming ? 'stop-button' : ''
				}`}
				disabled={!isStreaming && !value.trim()}>
				{isStreaming ? <StopIcon size={16} /> : <SendIcon size={16} />}
			</Button>
		</div>
	)
}

export default ChatInput
